import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment.development';
import { ErrorHandlingService } from '../../../core/services/error-handling.service';
import { BehaviorSubject, catchError, map, Observable, tap } from 'rxjs';
import { ApiRespones, Options, OrganizationLogo } from './settings.interface';
import { ApiResponse } from '../../../core/models/api-response.models';
import { AuditFieldChange } from '../../audit-logs/audit-logs.interface';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private readonly API_URL = `${environment.apiUrl}`;
  private readonly http = inject(HttpClient);
  private readonly errorHandler = inject(ErrorHandlingService);
  options: number[] = [];
  private readonly optionsSubject =
    new BehaviorSubject<ApiRespones<number> | null>(null);
  titles$ = this.optionsSubject.asObservable();

  getOptions(organizationId: number): Observable<ApiRespones<number>> {
    const params = this.buildQueryParams(organizationId);

    return this.http
      .get<
        ApiRespones<number>
      >(`${this.API_URL}/OrganizationOption/get-by-org-id`, { params })
      .pipe(
        tap({
          next: response => this.optionsSubject.next(response), // Emit fetched data to Subject
        }),
        catchError(error => this.errorHandler.handleError(error)) // Handle errors
      );
  }

  getOptionsLookup(): Observable<{ id: number; value: string }[]> {
    const params = new HttpParams().set('TypeName', 'Setting-Options');
    return this.http
      .get<
        ApiResponse<{ id: number; value: string }[]>
      >(`${this.API_URL}/applicationlookup`, { params })
      .pipe(
        map(response => response.result || []),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  private buildQueryParams(organizationId: number): HttpParams {
    let params = new HttpParams();

    if (organizationId) {
      params = params.set('organizationId', organizationId.toString());
    }

    return params;
  }

  updateOptions(paylod: Options): Observable<ApiRespones<null>> {
    return this.http
      .put<
        ApiRespones<null>
      >(`${this.API_URL}/OrganizationOption/update`, paylod)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getOrganizationLogos(organizationId: number): Observable<OrganizationLogo[]> {
    const params = new HttpParams().set(
      'organizationId',
      organizationId.toString()
    );

    return this.http
      .get<
        ApiResponse<OrganizationLogo[]>
      >(`${this.API_URL}/OrganizationLogo/get-by-org-id`, { params })
      .pipe(
        map(response => {
          if (!response.result) {
            throw new Error('No logos found');
          }
          return response.result;
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  updateOrganizationActiveLogo(
    organizationId: number,
    logoId: number,
    _modifiedFields?: AuditFieldChange[]
  ): Observable<OrganizationLogo> {
    const params = new HttpParams()
      .set('organizationId', organizationId.toString())
      .set('logoId', logoId.toString());

    const requestBody = _modifiedFields ? { _modifiedFields } : null;

    return this.http
      .put<ApiResponse<OrganizationLogo>>(
        `${this.API_URL}/OrganizationLogo/set-active-logo`,
        requestBody,
        {
          params,
        }
      )
      .pipe(
        map(response => {
          if (!response.result) {
            throw new Error('Logo update failed');
          }
          return response.result;
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  createOrganizationLogo(
    organizationId: number,
    productName: string,
    logoFile: File,
    _modifiedFields?: AuditFieldChange[]
  ): Observable<OrganizationLogo> {
    const formData = new FormData();
    formData.append('LogoFile', logoFile);
    formData.append('PrintLogoFile', '');
    if (_modifiedFields) {
      formData.append('_modifiedFields', JSON.stringify(_modifiedFields));
    }

    const params = new HttpParams()
      .set('ProductName', productName)
      .set('OrganizationId', organizationId.toString());

    return this.http
      .post<
        ApiResponse<OrganizationLogo>
      >(`${this.API_URL}/OrganizationLogo/upload-logo`, formData, { params })
      .pipe(
        map(response => {
          if (!response.result) {
            throw new Error('Logo creation failed');
          }
          return response.result;
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  updateLogo(
    logoId: number,
    productName: string,
    organizationId: number,
    logoFile: File,
    _modifiedFields?: AuditFieldChange[]
  ): Observable<OrganizationLogo> {
    const formData = new FormData();
    formData.append('LogoFile', logoFile);
    if (_modifiedFields) {
      formData.append('_modifiedFields', JSON.stringify(_modifiedFields));
    }

    const params = new HttpParams()
      .set('logoId', logoId.toString())
      .set('ProductName', productName)
      .set('OrganizationId', organizationId.toString());

    return this.http
      .put<
        ApiResponse<OrganizationLogo>
      >(`${this.API_URL}/OrganizationLogo/update-logo`, formData, { params })
      .pipe(
        map(response => {
          if (!response.result) {
            throw new Error('Logo update failed');
          }
          return response.result;
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  deleteOrganizationLogo(logoId: number): Observable<void> {
    return this.http
      .delete<void>(`${this.API_URL}/OrganizationLogo/${logoId}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }
}
